import {
  ChevronRight as ChevronRightIcon,
  ExpandMore as ExpandMoreIcon,
} from "@mui/icons-material";
import { Box, IconButton, Stack, Typography } from "@mui/material";
import type {
  ColDef,
  ICellRendererParams,
  IHeaderParams,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useMemo } from "react";
import { defaultColDef, defaultGridOptions } from "@/setup/ag-grid/mesf-theme";
import type { ScrapMixStatistic } from "../models";
import { formatWeight } from "../utils/formatWeight";
import { ChargeLink } from "./ChargeLink";

interface Props {
  statistics: ScrapMixStatistic[];
  isLoading: boolean;
  expandedRecipes: ReadonlySet<number>;
  onToggleRecipe: (recipeFamilyId: number) => void;
}

interface MaterialDefinition {
  key: string;
  name: string;
  color: string | null;
}

interface AnalysisTableRow {
  id: string;
  level: "recipe" | "charge";
  recipeFamilyId: number;
  recipeName: string;
  chargeNumber: number | null;
  sampleCount: number;
  values: Record<string, ScrapMixStatistic>;
  hasChildren: boolean;
}

const WeightCell = ({ value, data }: ICellRendererParams<AnalysisTableRow>) => {
  const statistic = value as ScrapMixStatistic | undefined;
  if (!statistic) return <Typography color="text.disabled">-</Typography>;

  const isUnexpected = statistic.IsUnexpectedMaterial;
  const alertTitle = isUnexpected
    ? data?.level === "charge"
      ? `Loaded material is not present in any theoretical recipe version for Charge ${data.chargeNumber} during the selected period.`
      : "Loaded material is not present in any theoretical recipe version during the selected period."
    : undefined;

  return (
    <Stack
      spacing={0}
      title={alertTitle}
      data-unexpected-material={isUnexpected || undefined}
      data-testid={
        isUnexpected && data
          ? `unexpected-material-${data.recipeFamilyId}-${data.level}-${data.chargeNumber ?? "all"}-${statistic.CommodityId}`
          : undefined
      }
      sx={{
        width: "100%",
        height: "100%",
        lineHeight: 1.15,
        py: 0.5,
        textAlign: "center",
        justifyContent: "center",
        bgcolor: isUnexpected ? "#ffebee" : undefined,
      }}
    >
      <Typography variant="body2" color="text.primary" sx={{ fontWeight: 600 }}>
        {formatWeight(statistic.AverageWeight)}
      </Typography>
      <Typography variant="caption" color="text.primary">
        <ChargeLink
          chargeId={statistic.MinimumChargeId}
          heatName={statistic.MinimumHeatName}
        >
          {formatWeight(statistic.MinimumWeight)}
        </ChargeLink>{" "}
        /{" "}
        <ChargeLink
          chargeId={statistic.MaximumChargeId}
          heatName={statistic.MaximumHeatName}
        >
          {formatWeight(statistic.MaximumWeight)}
        </ChargeLink>
      </Typography>
    </Stack>
  );
};

const MaterialHeader = (params: IHeaderParams) => {
  const color = (
    params.column.getColDef().headerComponentParams as { color?: string }
  )?.color;

  return (
    <Stack
      direction="row"
      spacing={0.75}
      alignItems="center"
      justifyContent="center"
      sx={{ height: "100%", width: "100%", textAlign: "center" }}
    >
      {color ? (
        <Box
          aria-hidden="true"
          sx={{
            width: 12,
            height: 12,
            bgcolor: color,
            borderRadius: "2px",
            flexShrink: 0,
          }}
        />
      ) : null}
      <Typography
        variant="caption"
        sx={{
          fontWeight: 600,
          lineHeight: 1.15,
          whiteSpace: "pre-line",
          overflowWrap: "break-word",
          textAlign: "center",
        }}
      >
        {params.displayName}
      </Typography>
    </Stack>
  );
};

export const CompositionTable = ({
  statistics,
  isLoading,
  expandedRecipes,
  onToggleRecipe,
}: Props) => {
  const { rows, materials } = useMemo(() => {
    const rowsById = new Map<string, AnalysisTableRow>();
    const materialsByKey = new Map<string, MaterialDefinition>();

    for (const statistic of statistics) {
      const rowId =
        statistic.Level === "recipe"
          ? `recipe-${statistic.RecipeFamilyId}`
          : `recipe-${statistic.RecipeFamilyId}-charge-${statistic.ChargeNumber}`;
      const valueKey = statistic.IsTotal
        ? "total"
        : `commodity-${statistic.CommodityId}`;
      const existing = rowsById.get(rowId) ?? {
        id: rowId,
        level: statistic.Level,
        recipeFamilyId: statistic.RecipeFamilyId,
        recipeName: statistic.RecipeName,
        chargeNumber: statistic.ChargeNumber,
        sampleCount: statistic.SampleCount,
        values: {},
        hasChildren: false,
      };

      existing.values[valueKey] = statistic;
      rowsById.set(rowId, existing);

      if (!statistic.IsTotal) {
        materialsByKey.set(valueKey, {
          key: valueKey,
          name: statistic.CommodityName,
          color: statistic.CommodityColor,
        });
      }
    }

    const allRows = Array.from(rowsById.values());
    const childFamilies = new Set(
      allRows
        .filter((row) => row.level === "charge")
        .map((row) => row.recipeFamilyId),
    );
    for (const row of allRows) {
      row.hasChildren =
        row.level === "recipe" && childFamilies.has(row.recipeFamilyId);
    }

    allRows.sort((left, right) => {
      const recipeOrder = left.recipeName.localeCompare(right.recipeName);
      if (recipeOrder !== 0) return recipeOrder;
      if (left.level !== right.level) return left.level === "recipe" ? -1 : 1;
      return (left.chargeNumber ?? 0) - (right.chargeNumber ?? 0);
    });

    return {
      rows: allRows,
      materials: Array.from(materialsByKey.values()).sort((left, right) =>
        left.name.localeCompare(right.name),
      ),
    };
  }, [statistics]);

  const visibleRows = useMemo(
    () =>
      rows.filter(
        (row) =>
          row.level === "recipe" || expandedRecipes.has(row.recipeFamilyId),
      ),
    [expandedRecipes, rows],
  );

  const columnDefs = useMemo<ColDef<AnalysisTableRow>[]>(
    () => [
      {
        headerName: "Recipe /\nCharge",
        width: 110,
        minWidth: 95,
        flex: 0,
        pinned: "left",
        wrapHeaderText: true,
        headerComponent: MaterialHeader,
        valueGetter: ({ data }) =>
          data?.level === "recipe"
            ? data.recipeName
            : `Ch. ${data?.chargeNumber ?? ""}`,
        cellRenderer: (params: ICellRendererParams<AnalysisTableRow>) => {
          const row = params.data;
          if (!row) return null;
          const isExpanded = expandedRecipes.has(row.recipeFamilyId);

          return (
            <Stack
              direction="row"
              alignItems="center"
              spacing={0.5}
              sx={{ height: "100%", width: "100%", minWidth: 0 }}
            >
              {row.level === "charge" ? (
                <>
                  <IconButton
                    size="small"
                    disabled
                    tabIndex={-1}
                    aria-hidden="true"
                    sx={{ visibility: "hidden" }}
                  >
                    <ChevronRightIcon fontSize="small" />
                  </IconButton>
                  <Box
                    title={`Charge ${row.chargeNumber}`}
                    sx={{
                      display: "inline-grid",
                      justifyItems: "center",
                      minWidth: 0,
                      overflow: "hidden",
                    }}
                  >
                    <Typography
                      variant="body2"
                      noWrap
                      aria-hidden="true"
                      sx={{
                        gridArea: "1 / 1",
                        fontWeight: 600,
                        visibility: "hidden",
                        maxWidth: "100%",
                      }}
                    >
                      {row.recipeName}
                    </Typography>
                    <Typography
                      variant="body2"
                      noWrap
                      sx={{ gridArea: "1 / 1" }}
                    >
                      {`Ch. ${row.chargeNumber}`}
                    </Typography>
                  </Box>
                </>
              ) : null}
              {row.level === "recipe" && row.hasChildren ? (
                <IconButton
                  size="small"
                  aria-label={`${isExpanded ? "Collapse" : "Expand"} ${row.recipeName}`}
                  onClick={(event) => {
                    event.stopPropagation();
                    onToggleRecipe(row.recipeFamilyId);
                  }}
                >
                  {isExpanded ? (
                    <ExpandMoreIcon fontSize="small" />
                  ) : (
                    <ChevronRightIcon fontSize="small" />
                  )}
                </IconButton>
              ) : null}
              {row.level === "recipe" ? (
                <Typography
                  variant="body2"
                  noWrap
                  title={row.recipeName}
                  sx={{ fontWeight: 600 }}
                >
                  {row.recipeName}
                </Typography>
              ) : null}
            </Stack>
          );
        },
        cellStyle: {
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
        },
      },
      {
        headerName: "Heats /\nBuckets",
        field: "sampleCount",
        width: 62,
        minWidth: 58,
        flex: 0,
        pinned: "left",
        wrapHeaderText: true,
        headerComponent: MaterialHeader,
        cellStyle: {
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        },
      },
      {
        headerName: "Total",
        colId: "total",
        width: 140,
        minWidth: 125,
        flex: 0,
        pinned: "left",
        valueGetter: ({ data }) => data?.values.total,
        cellRenderer: WeightCell,
        headerComponent: MaterialHeader,
        cellStyle: {
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        },
      },
      ...materials.map<ColDef<AnalysisTableRow>>((material) => ({
        headerName: material.name,
        colId: material.key,
        width: 130,
        minWidth: 115,
        flex: 0,
        wrapHeaderText: true,
        valueGetter: ({ data }) => data?.values[material.key],
        cellRenderer: WeightCell,
        headerComponent: MaterialHeader,
        headerComponentParams: { color: material.color },
        cellStyle: {
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        },
      })),
    ],
    [expandedRecipes, materials, onToggleRecipe],
  );

  return (
    <Box
      sx={{
        height: 430,
        width: "100%",
        "& .ag-horizontal-left-spacer:not(.ag-scroller-corner)": {
          overflowX: "auto",
        },
      }}
      data-testid="composition-table"
    >
      <AgGridReact<AnalysisTableRow>
        rowData={visibleRows}
        columnDefs={columnDefs}
        defaultColDef={{ ...defaultColDef, sortable: false, resizable: true }}
        getRowId={({ data }) => data.id}
        getRowStyle={({ data }) =>
          data?.level === "recipe" ? { backgroundColor: "#fafafa" } : undefined
        }
        rowHeight={54}
        headerHeight={48}
        loading={isLoading}
        overlayNoRowsTemplate="No scrap mix data matches the selected filters."
        {...defaultGridOptions}
        rowSelection={undefined}
      />
    </Box>
  );
};
